/**
 * "Labor rows" — the data behind one Overview bar.
 *
 * Every bar on the sliced chart is a sum of crosstab cells (person × home department ×
 * state × payer type × facility group × facility). This lists exactly those cells, with
 * the same month-range share and claim-filter weight the bar applied, so the total line
 * here IS the bar. Claim rows and Touch rows drill further, to the claims themselves;
 * this is the one drill that also reaches labor sitting on no claim, which has no claim
 * row to drill to.
 *
 * The no-claim bar has its own source grain, person × month (`Dataset.unattributedMonths`),
 * and opens on that: payroll by the month it was paid in. Those months are observed, not
 * split by share, so under a narrowed month range the two can differ — the line says so.
 */
import { useMemo, useState } from 'react'
import { DataTable, type Column } from '@/components/Table'
import { copyRows, downloadCSV, type ExportColumn } from '@/ui/export'
import { stateName } from '@/data/stateNames'
import type { UnattributedMonthRow } from '@/data/types'
import { int, money0, money2, monthYear } from '@/format'

export interface SliceCell {
  employee: string
  department: string
  state: string
  payer: string
  group: string
  facility: string
  /** After the bar's month-range share and claim-filter weight. */
  claims: number
  labor: number
}

const cpc = (c: { claims: number; labor: number }) => (c.claims >= 0.5 ? c.labor / c.claims : null)

const CELL_COLUMNS: Column<SliceCell>[] = [
  {
    key: 'employee',
    header: 'Person',
    sort: (r) => r.employee,
    render: (r) => r.employee,
  },
  { key: 'department', header: 'Department', sort: (r) => r.department, render: (r) => r.department, dim: true },
  { key: 'group', header: 'Facility group', sort: (r) => r.group, render: (r) => r.group, dim: true },
  { key: 'facility', header: 'Facility', sort: (r) => r.facility, render: (r) => r.facility, dim: true },
  { key: 'state', header: 'State', sort: (r) => r.state, render: (r) => stateName(r.state), dim: true },
  { key: 'payer', header: 'Payer type', sort: (r) => r.payer, render: (r) => r.payer, dim: true },
  { key: 'claims', header: 'Claims', align: 'right', sort: (r) => r.claims, render: (r) => int(r.claims) },
  { key: 'labor', header: 'Labor', align: 'right', sort: (r) => r.labor, render: (r) => money0(r.labor) },
  { key: 'cpc', header: 'Cost per claim', align: 'right', sort: cpc, render: (r) => money2(cpc(r)) },
]

const CELL_EXPORT: ExportColumn<SliceCell>[] = [
  { header: 'Person', value: (r) => r.employee },
  { header: 'Department', value: (r) => r.department },
  { header: 'Facility group', value: (r) => r.group },
  { header: 'Facility', value: (r) => r.facility },
  { header: 'State', value: (r) => r.state },
  { header: 'Payer type', value: (r) => r.payer },
  { header: 'Claims', value: (r) => Math.round(r.claims * 100) / 100 },
  { header: 'Labor', value: (r) => Math.round(r.labor * 100) / 100 },
]

const MONTH_COLUMNS: Column<UnattributedMonthRow>[] = [
  {
    key: 'employee',
    header: 'Person',
    sort: (r) => r.employee,
    render: (r) => r.employee,
  },
  { key: 'month', header: 'Month', sort: (r) => r.month, render: (r) => monthYear(r.month) },
  { key: 'department', header: 'Department', sort: (r) => r.department, render: (r) => r.department, dim: true },
  { key: 'labor', header: 'Labor on no claim', align: 'right', sort: (r) => r.labor, render: (r) => money0(r.labor) },
]

const MONTH_EXPORT: ExportColumn<UnattributedMonthRow>[] = [
  { header: 'Person', value: (r) => r.employee },
  { header: 'Month', value: (r) => r.month },
  { header: 'Department', value: (r) => r.department },
  { header: 'Labor on no claim', value: (r) => Math.round(r.labor * 100) / 100 },
]

export function SliceLaborRows({
  title,
  cells,
  months,
  narrowed,
  openPerson,
}: {
  /** The bar, as its label reads. */
  title: string
  cells: SliceCell[]
  /** The no-claim bar's person-month rows, when this is that bar and the bundle has them. */
  months: UnattributedMonthRow[] | null
  /** The month range leaves out months the bundle carries. */
  narrowed: boolean
  openPerson: (name: string) => void
}) {
  const [view, setView] = useState<'cells' | 'months'>(months ? 'months' : 'cells')
  const [note, setNote] = useState('')
  const showMonths = view === 'months' && months !== null

  const tot = useMemo(
    () => ({
      claims: cells.reduce((a, c) => a + c.claims, 0),
      labor: cells.reduce((a, c) => a + c.labor, 0),
      people: new Set(cells.map((c) => c.employee)).size,
      monthLabor: months?.reduce((a, r) => a + r.labor, 0) ?? 0,
      monthPeople: months ? new Set(months.map((r) => r.employee)).size : 0,
    }),
    [cells, months],
  )

  const file = `${title.replace(/[^\w]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'slice'}-labor-rows.csv`
  const exportNow = async (how: 'copy' | 'csv') => {
    const r = showMonths
      ? how === 'copy'
        ? await copyRows(months!, MONTH_EXPORT)
        : downloadCSV(file, months!, MONTH_EXPORT)
      : how === 'copy'
        ? await copyRows(cells, CELL_EXPORT)
        : downloadCSV(file, cells, CELL_EXPORT)
    setNote(r === 'ok' ? (how === 'copy' ? 'Copied' : 'Downloaded') : 'Blocked here — use Copy')
  }

  return (
    <div className="slice-rows">
      <div className="slice-rows-head">
        <div>
          {showMonths ? (
            <>
              <b>{int(months!.length)}</b> person-months · <b>{money0(tot.monthLabor)}</b> on no claim ·{' '}
              {int(tot.monthPeople)} people
              <span className="slice-rows-note">
                {narrowed
                  ? ` · the months it was paid in. The bar splits each person’s whole period by their share of the range (${money0(tot.labor)}), so the two differ under a range.`
                  : ' · adds up to the bar.'}
              </span>
            </>
          ) : (
            <>
              <b>{int(cells.length)}</b> rows · <b>{money0(tot.labor)}</b> labor · <b>{int(tot.claims)}</b> claims ·{' '}
              {int(tot.people)} people
              <span className="slice-rows-note">
                {' '}
                · adds up to the bar
                {narrowed ? '; each person’s whole period split by their share of the month range' : ''}.
              </span>
            </>
          )}
        </div>
        <div className="selbar-acts">
          {months && (
            <>
              <button type="button" className={view === 'months' ? 'on' : undefined} onClick={() => setView('months')}>
                By month
              </button>
              <button type="button" className={view === 'cells' ? 'on' : undefined} onClick={() => setView('cells')}>
                By person
              </button>
            </>
          )}
          <button type="button" onClick={() => void exportNow('copy')}>
            Copy
          </button>
          <button type="button" onClick={() => void exportNow('csv')}>
            CSV
          </button>
          {note && <span className="slice-rows-note">{note}</span>}
        </div>
      </div>
      {showMonths ? (
        <DataTable
          storageKey="slice-labor.no-claim"
          sortParam="ncrows.sort"
          rows={months!}
          columns={MONTH_COLUMNS}
          onRowClick={(r) => openPerson(r.employee)}
          rowKey={(r) => `${r.employee}|${r.month}|${r.department}`}
          initialSort="labor"
          maxHeight={420}
          empty="No payroll on no claim in this range."
        />
      ) : (
        <DataTable
          storageKey="slice-labor.rows"
          sortParam="rows.sort"
          rows={cells}
          columns={CELL_COLUMNS}
          onRowClick={(r) => openPerson(r.employee)}
          rowKey={(r) => `${r.employee}|${r.department}|${r.state}|${r.payer}|${r.group}|${r.facility}`}
          initialSort="labor"
          maxHeight={420}
          empty="No rows behind this bar."
        />
      )}
    </div>
  )
}
